/**
 * /portal — full ZeroConf wizard shell with all 7 steps mounted (Phase 5 complete).
 * - Steps 1–4: hardware → greenfield → WAN/LACP → VLANs/mgmt.
 * - Step 5: Web filter + outbound services.
 * - Step 6: Smart policies + L7 reachability + cloud-services matrix.
 * - Step 7: Generate & Export — terminal viewer + actions.
 * Right-hand LiveCodePreview is hidden on Step 7 so the terminal eats the full width.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef, Suspense, lazy } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft, ArrowRight, Pencil, Save, Loader2,
    Check, AlertCircle, Cpu, LogOut, LayoutDashboard, FlaskConical,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { Brand } from "../components/Brand";
import { useAuthStore } from "../state/authStore";
import { Stepper, STEPS } from "../zeroconf/components/Stepper";
import { LiveCodePreview } from "../zeroconf/components/LiveCodePreview";
import { isStep1Valid } from "../zeroconf/steps/Step1HardwareSetup";
import { isStep2Valid } from "../zeroconf/steps/Step2Greenfield";
import { isStep3Valid } from "../zeroconf/steps/Step3WanLacp";
import { isStep4Valid } from "../zeroconf/steps/Step4VlansManagement";
import { useWizardStore } from "../zeroconf/state/wizardStore";
import { useWizardClientLink } from "../zeroconf/state/wizardPrefill";
import { WizardEntryPicker } from "../zeroconf/components/WizardEntryPicker";
import { useAutoSave } from "../zeroconf/hooks/useAutoSave";
import { getJSON } from "../lib/api";
import { DEMO_PAYLOAD, DEMO_BACKUP_PREFIX } from "../zeroconf/data/demoPayload";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";

// Lazy-loaded step components keep the initial Portal bundle small.
const Step1HardwareSetup     = lazy(() => import("../zeroconf/steps/Step1HardwareSetup").then((m) => ({ default: m.Step1HardwareSetup })));
const Step2Greenfield        = lazy(() => import("../zeroconf/steps/Step2Greenfield").then((m) => ({ default: m.Step2Greenfield })));
const Step3WanLacp           = lazy(() => import("../zeroconf/steps/Step3WanLacp").then((m) => ({ default: m.Step3WanLacp })));
const Step4VlansManagement   = lazy(() => import("../zeroconf/steps/Step4VlansManagement").then((m) => ({ default: m.Step4VlansManagement })));
const Step5WebFilterOutbound = lazy(() => import("../zeroconf/steps/Step5WebFilterOutbound").then((m) => ({ default: m.Step5WebFilterOutbound })));
const Step5bSmartPolicies    = lazy(() => import("../zeroconf/steps/Step5bSmartPolicies").then((m) => ({ default: m.Step5bSmartPolicies })));
const Step6GenerateExport    = lazy(() => import("../zeroconf/steps/Step6GenerateExport").then((m) => ({ default: m.Step6GenerateExport })));

const VENDOR_TO_CLITYPE = {
    fortinet: "fortios", zyxel: "zyxel_zld", zyxel_uos: "zyxel_uos",
    watchguard: "watchguard", pfsense: "pfsense",
};

const TOTAL_STEPS = STEPS.length; // 7

const StepLoader = () => (
    <div className="flex items-center gap-2 text-text-muted text-[12.5px] py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-accent-cyan" /> Loading step…
    </div>
);

// ─── Top bar ────────────────────────────────────────────────────────────────
const TopBar = ({
    configName, onRenameSave, saveStatus, user, onSignOut, onSaveDraft,
    onLoadDemo, demoBusy, readOnly, isDemoRoute,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(configName || "Untitled draft");
    const navigate = useNavigate();
    useEffect(() => setDraft(configName || "Untitled draft"), [configName]);

    const submit = () => {
        const v = draft.trim() || "Untitled draft";
        setDraft(v); onRenameSave(v); setEditing(false);
    };

    const statusBlock = useMemo(() => {
        const map = {
            idle:   { text: readOnly ? "Read-only" : "Ready", tone: "text-text-dim", icon: null },
            saving: { text: "Saving…",          tone: "text-accent-cyan",  icon: <Loader2 className="w-3 h-3 animate-spin" /> },
            saved:  { text: "All changes saved", tone: "text-accent-mint", icon: <Check className="w-3 h-3" /> },
            error:  { text: "Save failed — retry", tone: "text-accent-red", icon: <AlertCircle className="w-3 h-3" /> },
        };
        return map[saveStatus] || map.idle;
    }, [saveStatus, readOnly]);

    return (
        <header className="sticky top-0 z-30 h-14 flex items-center px-4 border-b border-white/[0.06] bg-canvas/85 backdrop-blur-xl" data-testid="portal-topbar">
            <button type="button" onClick={() => navigate(user ? "/dashboard" : "/")}
                className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
                data-testid="portal-back-dashboard">
                <Brand className="h-7 w-7" />
                <span className="font-display font-semibold text-[14px] tracking-tight hidden sm:block">
                    <span className="text-accent-cyan">Cyber</span>Forefront
                </span>
            </button>
            <div className="mx-5 h-5 w-px bg-white/[0.08] hidden md:block" />
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-[0.22em] text-accent-cyan whitespace-nowrap">
                    <Cpu className="w-3 h-3" /> ZeroConf
                </div>
                {editing ? (
                    <input autoFocus value={draft} disabled={readOnly}
                        onChange={(e) => setDraft(e.target.value.slice(0, 80))} onBlur={submit}
                        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") { setDraft(configName); setEditing(false); } }}
                        className="bg-canvas border border-accent-cyan/40 rounded px-2 py-1 text-[13px] text-text-primary focus:outline-none w-60"
                        data-testid="portal-config-name-input" />
                ) : (
                    <button type="button" disabled={readOnly} onClick={() => setEditing(true)}
                        className="group flex items-center gap-1.5 text-[13.5px] text-text-primary hover:text-accent-cyan transition-colors min-w-0 disabled:cursor-not-allowed"
                        data-testid="portal-config-name">
                        <span className="truncate max-w-[18rem]">{configName || "Untitled draft"}</span>
                        {!readOnly && <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />}
                    </button>
                )}
                <div className={`hidden sm:inline-flex items-center gap-1.5 text-[11px] font-mono ${statusBlock.tone}`} data-testid="portal-save-status">
                    {statusBlock.icon}<span>{statusBlock.text}</span>
                </div>
                {!isDemoRoute && (
                    <button type="button" onClick={onLoadDemo} disabled={demoBusy}
                        data-testid="portal-load-demo"
                        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-amber/10 border border-accent-amber/30 text-accent-amber text-[11px] font-mono hover:bg-accent-amber/15 transition-colors disabled:opacity-50">
                        <FlaskConical className="w-3 h-3" /> Load demo data
                    </button>
                )}
            </div>

            <div className="hidden md:flex items-center gap-2 mr-4 text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                <span className="w-1.5 h-1.5 rounded-full bg-text-dim" /> Idle
            </div>

            <div className="flex items-center gap-1.5">
                {!readOnly && (
                    <button type="button" onClick={onSaveDraft}
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] text-[12px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30 transition-colors"
                        data-testid="portal-save-now">
                        <Save className="w-3.5 h-3.5" /> Save
                    </button>
                )}
                {user ? (
                    <Link to="/dashboard"
                        className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] text-text-muted hover:text-accent-cyan transition-colors"
                        data-testid="portal-dashboard-link">
                        <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
                    </Link>
                ) : (
                    <Link to="/signup"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12px] font-medium hover:shadow-glow-cyan transition-shadow"
                        data-testid="portal-signup-cta">
                        Sign up free
                    </Link>
                )}
                {user && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button type="button" onClick={onSignOut}
                                className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-text-muted hover:text-accent-red transition-colors"
                                data-testid="portal-signout" aria-label="Sign out">
                                <LogOut className="w-3.5 h-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-surface border-white/[0.08]">
                            Sign out · {user.email}
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </header>
    );
};

const ReadOnlyBanner = ({ reason }) => (
    <div className="px-5 py-2.5 bg-accent-amber/10 border-b border-accent-amber/30 flex items-center justify-between gap-3" data-testid="readonly-banner">
        <div className="flex items-center gap-2 text-[12.5px] text-accent-amber">
            <FlaskConical className="w-3.5 h-3.5" />
            <span>{reason || "Demo mode — read-only preview. Upgrade to edit and download."}</span>
        </div>
        <Link to="/pricing" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-amber text-canvas text-[12px] font-medium hover:opacity-90 transition-opacity">
            Upgrade <ArrowRight className="w-3 h-3" />
        </Link>
    </div>
);

const DemoConfirmDialog = ({ open, onConfirm, onCancel }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="demo-confirm">
            <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="bg-surface border border-white/[0.08] rounded-xl p-6 max-w-md mx-4 shadow-heavy-popover">
                <div className="flex items-start gap-3">
                    <FlaskConical className="w-5 h-5 text-accent-cyan shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-display font-semibold text-[16px]">Replace current draft with demo?</h3>
                        <p className="mt-2 text-[13px] text-text-muted">
                            Your current draft will be saved as <span className="text-text-primary font-mono">"{DEMO_BACKUP_PREFIX} {new Date().toLocaleString()}"</span>.
                            The demo loads a complete Fortinet 60F config (4 VLANs, dual-WAN, LACP, mgmt network, smart policies, L7 rules).
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={onCancel} data-testid="demo-cancel"
                        className="px-3.5 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-text-primary">
                        Cancel
                    </button>
                    <button onClick={onConfirm} data-testid="demo-confirm"
                        className="px-3.5 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan">
                        Yes, load demo
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

// ─── Main Portal ────────────────────────────────────────────────────────────
export const Portal = ({ forceReadOnly = false, forceDemo = false }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuthStore();
    const wizard = useWizardStore();
    const { vendor, modello, currentStep, setStep, getPayload, loadSavedConfig, generated_script } = wizard;

    const userIsSubscriber =
        user && ["pro", "msp"].includes(user.subscription_plan) &&
        ["active", "trialing"].includes(user.subscription_status);
    const readOnly = forceReadOnly || (!!user && !userIsSubscriber);

    const [hardwareData, setHardwareData] = useState(null);
    const [hardwareErr, setHardwareErr] = useState(null);
    const [configName, setConfigName] = useState("Untitled draft");
    const [hydrated, setHydrated] = useState(false);
    const [demoBusy, setDemoBusy] = useState(false);
    const [demoConfirm, setDemoConfirm] = useState(false);
    const [visitedSteps, setVisitedSteps] = useState(new Set([1]));

    // Mark step as visited when the user lands on it
    useEffect(() => {
        setVisitedSteps((prev) => {
            if (prev.has(currentStep)) return prev;
            const next = new Set(prev); next.add(currentStep); return next;
        });
    }, [currentStep]);

    // Hydrate latest draft
    const didHydrate = useRef(false);
    useEffect(() => {
        if (didHydrate.current) return;
        didHydrate.current = true;
        if (forceDemo) { setHydrated(true); return; }
        if (!user) { setHydrated(true); return; }
        (async () => {
            try {
                const list = await getJSON("/zeroconf/configs");
                if (list.items?.length > 0) {
                    const latest = list.items[0];
                    setConfigName(latest.name);
                    if (latest.wizard_state && Object.keys(latest.wizard_state).length > 0) {
                        loadSavedConfig(latest.wizard_state);
                    }
                }
            } catch (e) { console.error("Failed to hydrate draft:", e); }
            finally { setHydrated(true); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, forceDemo]);

    // Fetch HAL
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await getJSON("/zeroconf/hardware");
                if (!cancelled) setHardwareData(data);
            } catch (e) {
                if (cancelled) return;
                if (forceDemo || !user) {
                    try {
                        const mod = await import("../zeroconf/hardware/hardwareDictionary");
                        const vendors = (mod.default || []).map((v) => ({
                            vendor: v.vendor, vendorCode: v.vendorCode, cliType: v.cliType, models: v.models,
                        }));
                        setHardwareData({ vendors, total_models: vendors.reduce((s, v) => s + v.models.length, 0) });
                    } catch (e2) {
                        setHardwareErr("Failed to load hardware list");
                    }
                } else {
                    setHardwareErr(e?.message || "Failed to load hardware list");
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Default mgmt port auto-fill when model changes
    useEffect(() => {
        if (!hardwareData || !vendor || !modello) return;
        const v = hardwareData.vendors?.find((x) => x.vendorCode === vendor);
        const m = v?.models?.find((x) => x.name === modello);
        if (m && !wizard.mgmt_port) {
            const defaultMgmt = m.defaultMgmtPort || m.defaultLanPort || m.ports?.[0]?.name;
            if (defaultMgmt) wizard.updateStep1({ mgmt_port: defaultMgmt });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hardwareData, vendor, modello]);

    // Phase 7c.4 — linked IPAM client (if any). Persisted alongside the
    // wizard draft so the saved config can be found via the client's "Recent
    // configs" panel. Each field selected separately so the Zustand selector
    // returns a primitive — selecting an object literal would cause a render
    // loop (new reference every time).
    const linkedClientId = useWizardClientLink((s) => s.client_id);
    const linkedClientName = useWizardClientLink((s) => s.client_name);
    const linkedSource = useWizardClientLink((s) => s.source);

    // Auto-save
    const autoSave = useAutoSave({
        configId: null, configName, debounceMs: 1500,
        getPayload: () => ({
            ...getPayload(),
            currentStep,
            client_id: linkedClientId,
            source: linkedSource,
        }),
    });

    // Phase 7c.4 — when the linked client changes (either via the picker or
    // the Unlink button), kick off a save so the new `client_id` lands on
    // disk. Without this the save runs with stale closure values.
    useEffect(() => {
        if (autoSave?.trigger) autoSave.trigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedClientId, linkedSource]);

    useEffect(() => {
        if (!hydrated || readOnly || !user) return;
        autoSave.trigger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hydrated, readOnly, user,
        vendor, modello, currentStep, configName,
        wizard.hostname, wizard.password_admin, wizard.dns_primary, wizard.dns_secondary,
        wizard.timezone_id, wizard.mgmt_port, wizard.mgmt_ip, wizard.mgmt_subnet,
        wizard.destroy_switch, wizard.remove_dhcp, wizard.disable_fortilink,
        wizard.wan1, wizard.wan2, wizard.lacp_aggregates,
        wizard.vlans, wizard.managementNetwork,
        // Phase 5 fields
        wizard.webfilter_config, wizard.outbound_services, wizard.policies, wizard.policy_config,
        wizard.smart_policies, wizard.app_reachability, wizard.cloud_services_mapping,
        wizard.generated_script,
    ]);

    // Validation per step
    const stepValidityMap = useMemo(() => ({
        1: isStep1Valid(wizard),
        2: isStep2Valid(wizard),
        3: isStep3Valid(wizard),
        4: isStep4Valid(wizard),
        5: true,
        6: true,
        7: !!generated_script,
    }), [wizard, generated_script]);

    // A step is unlocked when ALL prior steps' validation has passed.
    const unlockedSteps = useMemo(() => {
        const s = new Set([1]);
        if (stepValidityMap[1]) s.add(2);
        if (s.has(2) && stepValidityMap[2]) s.add(3);
        if (s.has(3) && stepValidityMap[3]) s.add(4);
        if (s.has(4) && stepValidityMap[4]) s.add(5);
        if (s.has(5) && stepValidityMap[5]) s.add(6);
        if (s.has(6) && stepValidityMap[6]) s.add(7);
        return s;
    }, [stepValidityMap]);

    // A step gets the "completed" mint badge once it has passed validation AND the user has visited it.
    const completedSteps = useMemo(() => {
        const s = new Set();
        for (let n = 1; n <= TOTAL_STEPS; n++) {
            if (stepValidityMap[n] && visitedSteps.has(n)) s.add(n);
        }
        return s;
    }, [stepValidityMap, visitedSteps]);

    const onStepSelect = useCallback((n) => {
        if (n === currentStep) return;
        if (!unlockedSteps.has(n)) return;
        setStep(n);
    }, [currentStep, setStep, unlockedSteps]);

    const onSignOut = useCallback(async () => {
        try { await logout(); } finally { navigate("/login"); }
    }, [logout, navigate]);

    // Demo loader
    const loadDemo = useCallback(async () => {
        setDemoBusy(true);
        try {
            if (userIsSubscriber) {
                try {
                    const ts = new Date().toLocaleString();
                    const payload = { ...getPayload(), currentStep };
                    await import("../lib/api").then(({ postJSON }) =>
                        postJSON("/zeroconf/configs", {
                            name: `${DEMO_BACKUP_PREFIX} ${ts}`,
                            vendor: payload.vendor || "",
                            model: payload.modello || "",
                            current_step: payload.currentStep || 1,
                            wizard_state: payload,
                        })
                    );
                    toast.success("Current draft archived");
                } catch (e) { console.error("backup failed", e); }
            }
            loadSavedConfig(DEMO_PAYLOAD);
            toast.success("Demo configuration loaded");
        } finally {
            setDemoBusy(false);
            setDemoConfirm(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIsSubscriber, getPayload, currentStep, loadSavedConfig]);

    const handleDemoClick = () => {
        if (!user) { navigate("/portal/demo"); return; }
        if (userIsSubscriber) { setDemoConfirm(true); return; }
        loadDemo();
    };

    const canGoNext = currentStep < TOTAL_STEPS && stepValidityMap[currentStep] && !readOnly;
    const isLastStep = currentStep === TOTAL_STEPS;

    const cliType = VENDOR_TO_CLITYPE[vendor];
    const livePayload = useMemo(() => getPayload(), [
        vendor, modello,
        wizard.hostname, wizard.password_admin, wizard.dns_primary, wizard.dns_secondary,
        wizard.timezone_id, wizard.mgmt_port, wizard.mgmt_ip, wizard.mgmt_subnet,
        wizard.destroy_switch, wizard.remove_dhcp, wizard.disable_fortilink,
        wizard.wan1, wizard.wan2, wizard.lacp_aggregates,
        wizard.vlans, wizard.managementNetwork,
        wizard.webfilter_config, wizard.outbound_services, wizard.policies, wizard.policy_config,
        wizard.smart_policies, wizard.app_reachability, wizard.cloud_services_mapping,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]);

    return (
        <TooltipProvider delayDuration={300}>
            {!forceDemo && !readOnly && (
                <WizardEntryPicker
                    onResolved={() => autoSave.trigger()}
                />
            )}
            <div className="min-h-screen bg-canvas text-text-primary flex flex-col" data-testid="portal-page">
                <SEO noindex title="ZeroConf Portal" canonicalPath="/portal" />
                <TopBar
                    configName={forceDemo ? "ZeroConf Demo (read-only)" : configName}
                    onRenameSave={(v) => { setConfigName(v); autoSave.trigger(); }}
                    saveStatus={autoSave.status} user={user}
                    onSignOut={onSignOut} onSaveDraft={() => autoSave.saveNow()}
                    onLoadDemo={handleDemoClick} demoBusy={demoBusy}
                    readOnly={readOnly} isDemoRoute={forceDemo}
                />

                {linkedClientId && linkedClientName && (
                    <div
                        className="px-4 md:px-6 py-1.5 bg-accent-cyan/[0.05] border-b border-accent-cyan/20 text-[12px] text-text-muted flex items-center gap-3"
                        data-testid="portal-client-link"
                    >
                        <span className="inline-flex items-center gap-1.5">
                            <Cpu className="w-3 h-3 text-accent-cyan" />
                            Linked to <span className="text-accent-cyan font-medium">{linkedClientName}</span>
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                useWizardClientLink.getState().clearClientLink();
                                toast.info("Unlinked — config will save as standalone");
                                autoSave.trigger();
                            }}
                            className="ml-auto text-text-dim hover:text-text-primary text-[11.5px] underline-offset-2 hover:underline"
                            data-testid="portal-unlink"
                        >
                            Unlink
                        </button>
                    </div>
                )}

                {readOnly && (
                    <ReadOnlyBanner reason={
                        forceDemo
                            ? "ZeroConf live demo — read-only. Sign up free to save your own config."
                            : "Demo mode — read-only preview. Upgrade to Pro to edit and download."
                    } />
                )}

                <div className="flex flex-1 min-h-0">
                    <aside className="hidden md:block w-64 shrink-0 border-r border-white/[0.05] bg-canvas overflow-y-auto" data-testid="portal-sidebar">
                        <Stepper currentStep={currentStep} completedSteps={completedSteps}
                            unlockedSteps={unlockedSteps} onSelect={onStepSelect} />
                    </aside>

                    {/* Mobile step indicator */}
                    <div className="md:hidden absolute top-[3.5rem] left-0 right-0 z-20 px-3 py-2 bg-canvas/85 backdrop-blur-md border-b border-white/[0.06] flex items-center gap-2 overflow-x-auto" data-testid="portal-mobile-stepper">
                        {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                            const isActive = currentStep === n;
                            const isUnlocked = unlockedSteps.has(n);
                            return (
                                <button key={n} type="button" disabled={!isUnlocked || isActive}
                                    onClick={() => onStepSelect(n)} data-testid={`mobile-step-${n}`}
                                    className={["shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full text-[11px] font-mono border transition-colors",
                                        isActive ? "bg-accent-cyan text-canvas border-accent-cyan" :
                                        isUnlocked ? "bg-canvas border-white/[0.12] text-text-muted hover:text-accent-cyan" :
                                        "bg-canvas border-white/[0.04] text-text-dim opacity-50 cursor-not-allowed",
                                    ].join(" ")}>{n}</button>
                            );
                        })}
                    </div>

                    <main className="flex-1 overflow-y-auto overflow-x-hidden relative md:pt-0 pt-12 min-w-0" data-testid="portal-console">
                        <div className={`${isLastStep ? "max-w-6xl" : "max-w-4xl"} mx-auto px-4 sm:px-6 md:px-8 py-10 pb-32`}>
                            {hardwareErr ? (
                                <div className="rounded-md border border-accent-red/30 bg-accent-red/5 p-4 text-[13px] text-accent-red">
                                    Failed to load hardware list: {hardwareErr}
                                </div>
                            ) : !hardwareData ? (
                                <div className="flex items-center gap-2 text-text-muted text-[13px]">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading hardware…
                                </div>
                            ) : (
                                <Suspense fallback={<StepLoader />}>
                                    {currentStep === 1 && <Step1HardwareSetup hardwareData={hardwareData} readOnly={readOnly} />}
                                    {currentStep === 2 && <Step2Greenfield readOnly={readOnly} />}
                                    {currentStep === 3 && <Step3WanLacp hardwareData={hardwareData} readOnly={readOnly} />}
                                    {currentStep === 4 && <Step4VlansManagement hardwareData={hardwareData} readOnly={readOnly} />}
                                    {currentStep === 5 && <Step5WebFilterOutbound readOnly={readOnly} />}
                                    {currentStep === 6 && <Step5bSmartPolicies readOnly={readOnly} />}
                                    {currentStep === 7 && (
                                        <Step6GenerateExport readOnly={readOnly} configName={configName}
                                            onConfigNameChange={(v) => { setConfigName(v); autoSave.trigger(); }} />
                                    )}
                                </Suspense>
                            )}
                        </div>

                        <div className="sticky bottom-0 left-0 right-0 px-6 sm:px-8 py-3.5 border-t border-white/[0.05] bg-canvas/90 backdrop-blur-md flex items-center justify-between">
                            <button type="button" disabled={currentStep === 1 || readOnly}
                                onClick={() => setStep(Math.max(1, currentStep - 1))}
                                data-testid="wizard-back"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12.5px] border border-white/[0.08] text-text-muted hover:text-text-primary hover:border-white/[0.15] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                <ArrowLeft className="w-3.5 h-3.5" /> Back
                            </button>
                            {!readOnly && (
                                <button type="button" onClick={() => autoSave.saveNow()}
                                    className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12.5px] text-text-muted hover:text-accent-cyan transition-colors"
                                    data-testid="wizard-save-draft">
                                    <Save className="w-3.5 h-3.5" /> Save draft
                                </button>
                            )}
                            {!isLastStep ? (
                                <button type="button" disabled={!canGoNext}
                                    onClick={() => setStep(Math.min(TOTAL_STEPS, currentStep + 1))}
                                    data-testid="wizard-next"
                                    className={["inline-flex items-center gap-1.5 px-5 py-2 rounded-md text-[12.5px] font-medium transition-all",
                                        canGoNext ? "bg-accent-cyan text-canvas hover:shadow-glow-cyan"
                                                  : "bg-white/[0.04] text-text-dim cursor-not-allowed",
                                    ].join(" ")}>
                                    Next <ArrowRight className="w-3.5 h-3.5" />
                                </button>
                            ) : (
                                <Link to={user ? "/dashboard" : "/"}
                                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md text-[12.5px] font-medium bg-accent-mint/15 border border-accent-mint/40 text-accent-mint hover:bg-accent-mint/25 transition-colors"
                                    data-testid="wizard-done">
                                    Done <Check className="w-3.5 h-3.5" />
                                </Link>
                            )}
                        </div>
                    </main>

                    {!isLastStep && (
                        <div className="hidden lg:block">
                            <LiveCodePreview payload={livePayload} cliType={cliType}
                                vendor={vendor} model={modello} hostname={wizard.hostname} />
                        </div>
                    )}
                </div>

                <DemoConfirmDialog
                    open={demoConfirm}
                    onConfirm={loadDemo}
                    onCancel={() => setDemoConfirm(false)}
                />
            </div>
        </TooltipProvider>
    );
};

export default Portal;
